import React, { useRef, useEffect, useState, useCallback } from "react";
import QrScanner from 'qr-scanner';
import {
  Box,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Stack
} from "@mui/material";
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useTranslation } from "react-i18next";
import { getCameraWithClosestFocus } from '../utils/cameraUtils';

interface SensorQrData {
  sn: string;
  imei: string;
  ccid: string;
  date: string;
}

interface QrScannerComponentProps {
  onScanSuccess: (data: SensorQrData) => void;
  children?: React.ReactNode;
}

const QrScannerComponent: React.FC<QrScannerComponentProps> = ({ onScanSuccess, children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // États pour le dialog
  const [isOpen, setIsOpen] = useState(false);
  
  // États pour le scanner
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasFlash, setHasFlash] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  
  // État pour la caméra optimale
  const [optimalCameraId, setOptimalCameraId] = useState<string | undefined>(undefined);
  const [isSelectingCamera, setIsSelectingCamera] = useState(false);
  
  // États pour la saisie manuelle
  const [manualQrValue, setManualQrValue] = useState('');
  
  // États pour la prise de photo et scan QR
  const [photoScanMode, setPhotoScanMode] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [scanningPhoto, setScanningPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse and validate QR code data
  const parseQrCode = useCallback((decodedText: string): SensorQrData | null => {
    try {
      const jsonData = JSON.parse(decodedText) as SensorQrData;
      if (!jsonData.sn || !jsonData.imei || !jsonData.ccid) {
        setError(t("qrcode.errors.missingFields", "QR code incomplet: champs manquants") || "QR code incomplet: champs manquants");
        return null;
      }
      return jsonData;
    } catch (e) {
      setError(t("qrcode.errors.invalidFormat", "Format de QR code invalide") || "Format de QR code invalide");
      return null;
    }
  }, [t]);


  // Fonction de démarrage du scanner avec vérification de l'élément vidéo
  const startScanning = useCallback(async () => {
    // Attendre que l'élément vidéo soit disponible
    let attempts = 0;
    const maxAttempts = 10;
    
    while (!videoRef.current && attempts < maxAttempts) {
      console.log(`Tentative ${attempts + 1}: Attente de l'élément vidéo...`);
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!videoRef.current) {
      console.error('Élément vidéo non disponible après', maxAttempts, 'tentatives');
      setError('Impossible d\'accéder à l\'élément vidéo');
      return;
    }

    try {
      setError('');
      console.log('Démarrage du scanner QR...');

      // Configuration pour résoudre les problèmes de ChunkLoadError sur Windows/production
      // Désactiver les Web Workers qui causent des problèmes de chargement
      // @ts-ignore - Désactiver temporairement les workers pour éviter les ChunkLoadError
      QrScanner.WORKER_PATH = undefined;

      // Détecter si on est sur desktop
      const isDesktop = !(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
      
      const qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('QR Code détecté:', result.data);
          const sensorData = parseQrCode(result.data);
          if (sensorData) {
            onScanSuccess(sensorData);
            setIsOpen(false);
          }
        },
        {
          onDecodeError: (err: any) => {
            // Réduire les logs pour éviter le spam
            if (!err.toString().includes('No QR code found')) {
              console.log('Decode error:', err);
            }
          },
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: optimalCameraId,
          // Paramètres optimisés pour desktop
          maxScansPerSecond: isDesktop ? 10 : 5, // Plus de scans par seconde sur desktop
          returnDetailedScanResult: true, // Obtenir plus de détails sur les résultats
          // Configuration spéciale pour desktop
          ...(isDesktop && {
            calculateScanRegion: (video) => {
              // Sur desktop, scanner toute la zone vidéo car les webcams ont souvent une résolution plus faible
              // et les QR codes peuvent être plus petits à l'écran
              return {
                x: 0,
                y: 0,
                width: video.videoWidth,
                height: video.videoHeight,
              };
            },
          }),
        }
      );

      qrScannerRef.current = qrScanner;

      await qrScanner.start();
      setIsScanning(true);
      console.log('Scanner démarré avec succès');

      // Vérifier si la torche est disponible
      const hasFlashSupport = await qrScanner.hasFlash();
      setHasFlash(hasFlashSupport);

    } catch (err: any) {
      console.error('Erreur lors du démarrage du scanner:', err);
      setError('Erreur lors du démarrage du scanner: ' + err.message);
    }
  }, [parseQrCode, onScanSuccess, optimalCameraId]);

  // Fonction d'arrêt du scanner (identique à la page de test)
  const stopScanning = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
    setFlashEnabled(false);
  }, []);

  // Fonction de contrôle de la torche (identique à la page de test)
  const toggleFlash = async () => {
    if (qrScannerRef.current && hasFlash) {
      try {
        if (flashEnabled) {
          await qrScannerRef.current.turnFlashOff();
          setFlashEnabled(false);
        } else {
          await qrScannerRef.current.turnFlashOn();
          setFlashEnabled(true);
        }
      } catch (err: any) {
        setError('Erreur lors du contrôle de la torche: ' + err.message);
      }
    }
  };


  // Vérifier la disponibilité des caméras au montage
  useEffect(() => {
    if (!QrScanner.hasCamera()) {
      setError('Aucune caméra détectée sur cet appareil');
      return;
    }
    
    // Fonction pour sélectionner la caméra optimale (définie à l'intérieur pour éviter les re-renders)
    const selectOptimalCamera = async () => {
      try {
        setIsSelectingCamera(true);
        console.log('Recherche de la caméra avec la distance de focus la plus courte...');
        
        const bestCameraId = await getCameraWithClosestFocus();
        
        if (bestCameraId) {
          setOptimalCameraId(bestCameraId);
          console.log('Caméra optimale sélectionnée:', bestCameraId);
        } else {
          console.log('Aucune caméra optimale trouvée, utilisation de la caméra par défaut');
          // Détecter le type de plateforme
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          const isDesktop = !isMobile;
          
          if (isDesktop) {
            // Sur desktop, laisser le navigateur choisir la meilleure caméra par défaut
            // Cela permet au navigateur d'utiliser sa propre logique de sélection
            setOptimalCameraId(undefined);
            console.log('Desktop détecté: laisser le navigateur choisir la caméra par défaut');
          } else {
            // Sur mobile, préférer environment (caméra arrière)
            setOptimalCameraId('environment');
            console.log('Mobile détecté: utilisation de la caméra environment');
          }
        }
      } catch (err) {
        console.error('Erreur lors de la sélection de la caméra optimale:', err);
        setError('Erreur lors de la sélection de la caméra optimale');
        
        // Fallback intelligent selon la plateforme
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isDesktop = !isMobile;
        
        if (isDesktop) {
          setOptimalCameraId(undefined); // Laisser le navigateur choisir sur desktop
          console.log('Erreur: Fallback desktop - laisser le navigateur choisir');
        } else {
          setOptimalCameraId('environment'); // Mobile fallback
          console.log('Erreur: Fallback mobile - utilisation environment');
        }
      } finally {
        setIsSelectingCamera(false);
      }
    };
    
    // Sélectionner la caméra optimale seulement une fois au montage
    selectOptimalCamera();
  }, []); // Dépendances vides pour n'exécuter qu'une fois

  // Gérer l'ouverture/fermeture du dialog
  useEffect(() => {
    if (isOpen && (optimalCameraId !== undefined || optimalCameraId === undefined) && !isSelectingCamera) {
      // Démarrer le scanner avec un délai pour s'assurer que la caméra est sélectionnée
      // Note: optimalCameraId peut être undefined sur desktop (laisse le navigateur choisir)
      const timer = setTimeout(() => {
        startScanning();
      }, 500);
      
      return () => {
        clearTimeout(timer);
        stopScanning();
      };
    } else {
      // Arrêter le scanner quand le dialog se ferme
      stopScanning();
    }
  }, [isOpen, optimalCameraId, isSelectingCamera, startScanning, stopScanning]);

  // Handler pour la saisie manuelle
  const handleManualQr = () => {
    const sensorData = parseQrCode(manualQrValue);
    if (sensorData) {
      onScanSuccess(sensorData);
      setIsOpen(false);
      setManualQrValue('');
    }
  };

  // Handler pour la prise de photo
  const handlePhotoCapture = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('sensor.photo.invalidFileType') || 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('sensor.photo.fileTooLarge') || 'File size must be less than 5MB');
      return;
    }

    try {
      setError('');
      setScanningPhoto(true);
      
      // Read image as data URL
      const reader = new FileReader();
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        setCapturedPhoto(result);
        
        // Try to scan QR code from the image
        try {
          const qrResult = await QrScanner.scanImage(file);
          console.log('QR Code found in photo:', qrResult);
          
          const sensorData = parseQrCode(qrResult);
          if (sensorData) {
            onScanSuccess(sensorData);
            setIsOpen(false);
            setCapturedPhoto(null);
            setPhotoScanMode(false);
          }
        } catch (qrError) {
          console.error('No QR code found in image:', qrError);
          setError(t('qrcode.noQrFoundInPhoto') || 'No QR code found in the selected photo. Please try another image.');
        } finally {
          setScanningPhoto(false);
        }
      };
      
      reader.onerror = () => {
        setError(t('sensor.photo.fileReadError') || 'Error reading file');
        setScanningPhoto(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      setError(t('sensor.photo.fileProcessError') || 'Error processing file');
      setScanningPhoto(false);
    }

    // Reset file input
    event.target.value = '';
  }, [parseQrCode, onScanSuccess, t]);

  // Handler pour ouvrir la prise de photo
  const handleOpenPhotoScan = () => {
    setPhotoScanMode(true);
    fileInputRef.current?.click();
  };

  return (
    <>
      {/* Bouton pour ouvrir le scanner */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('qrcode.scanTitle', 'Scanner QR Code')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('qrcode.scanDescription', 'Scannez le QR code du capteur pour remplir automatiquement les informations.')}
          </Typography>
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <Button
              variant="contained"
              startIcon={<QrCodeScannerIcon />}
              onClick={() => setIsOpen(true)}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              {t('qrcode.scanButton', 'Scanner QR Code')}
            </Button>
            <Button
              variant="outlined"
              startIcon={scanningPhoto ? <CircularProgress size={20} /> : <CameraAltIcon />}
              onClick={handleOpenPhotoScan}
              disabled={scanningPhoto}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              {scanningPhoto
                ? t('common.loading', 'Chargement...')
                : isMobile
                  ? t('qrcode.takePhotoToScan', 'Prendre une photo pour scanner')
                  : t('qrcode.importPhotoToScan', 'Importer une photo pour scanner')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Contenu intercalé (formulaire) */}
      {children}

      {/* Dialog du scanner */}
      <Dialog 
        open={isOpen} 
        onClose={() => setIsOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            height: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '100vh' : '90vh',
            m: isMobile ? 0 : 2,
          } 
        }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('qrcode.dialogTitle', 'Scanner QR Code')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasFlash && (
                <IconButton
                  onClick={toggleFlash}
                  color={flashEnabled ? "primary" : "default"}
                  disabled={!isScanning}
                >
                  {flashEnabled ? <FlashOnIcon /> : <FlashOffIcon />}
                </IconButton>
              )}
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Erreurs */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Informations sur la sélection de caméra */}
          {isSelectingCamera && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Sélection de la caméra optimale...
              </Typography>
              <CircularProgress size={20} sx={{ ml: 1 }} />
            </Box>
          )}
          
          {optimalCameraId && !isSelectingCamera && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Caméra optimisée pour le scan QR
              </Typography>
            </Box>
          )}

          {/* Zone de scan - Identique à la page de test */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              aspectRatio: '1',
              backgroundColor: 'grey.100',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
              flex: isMobile ? 1 : 'none'
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              playsInline
              muted
            />
            {(!isScanning || isSelectingCamera) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}
              >
                <QrCodeScannerIcon sx={{ fontSize: 64, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isSelectingCamera ? 'Sélection de la caméra optimale...' : 'Initialisation de la caméra...'}
                </Typography>
                <CircularProgress sx={{ mt: 2 }} />
              </Box>
            )}
          </Box>

          {/* Saisie manuelle */}
          <Box sx={{ mt: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('qrcode.manualEntry', 'Saisie manuelle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('qrcode.manualEntryDesc', 'Collez le contenu JSON du QR code ici :')}
            </Typography>
            <textarea
              value={manualQrValue}
              onChange={e => setManualQrValue(e.target.value)}
              rows={3}
              style={{ 
                width: "100%", 
                fontFamily: "monospace",
                fontSize: "14px",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                marginBottom: "8px"
              }}
              placeholder='{"sn":"...","imei":"...","ccid":"...","date":"..."}'
            />
            <Button
              variant="outlined"
              onClick={handleManualQr}
              fullWidth
              startIcon={<ContentPasteIcon />}
              disabled={!manualQrValue.trim()}
            >
              {t('qrcode.validate', 'Valider')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Hidden file input for photo capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment" // Prefer back camera on mobile
        style={{ display: 'none' }}
        onChange={handlePhotoCapture}
      />
    </>
  );
};

export default QrScannerComponent;